import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {PageControlBar} from 'shells/control-bar/page-control-bar';
import type {Tab} from 'shells/control-bar/page-control-bar';
import {StatusBarSecondaryTimestamp} from 'shells/control-bar/status-bar-secondary-timestamp';
import {SidebarIconReportsIcon} from 'shells/icon';
// @ts-expect-error - imported from .js/.jsx file
import {BookingIcon} from 'shells/icon/svg/booking-icon';
import {ContactsCircleIcon} from 'shells/icon/svg/contacts-circle-icon';
// @ts-expect-error - imported from .js/.jsx file
import {PencilToPaperIcon} from 'shells/icon/svg/pencil-to-paper-icon';
// @ts-expect-error - imported from .js/.jsx file
import {SchedulerIcon} from 'shells/icon/svg/scheduler-icon';
// @ts-expect-error - imported from .js/.jsx file
import {SolidClockIcon} from 'shells/icon/svg/solid-clock-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FormattedTimestamp} from 'shells/timestamp/formatted-timestamp';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import type {MeetingFragment} from 'nutshell-graphql-types';

import {analyticsClient} from '../../analytics-client';
import {appHistory} from '../../history';

import {useGetInstanceInfo} from '../core/use-get-instance-info';
import {useGetSessionUser} from '../core/use-get-session-user';
import {PageContainer, PageTitleBar} from '../layout';
import type {ReportURLParams} from '../reports/report-types';

import {AdvancedOptions} from './advanced-options/advanced-options';
// @ts-expect-error
import {Assignment} from './assignment';
import {BookingsTable} from './bookings/bookings-table';
import {useUpdateMeeting} from './graphql/hooks';
import {getStatusBadge} from './helpers';
import {getStatusBarPrimaryText} from './managed-meetings/helpers';
import {MeetingDetails} from './meeting-details';
import {MeetingForm} from './meeting-form';
import {MeetingPageButtons} from './meeting-page-buttons';
import {PeanutScheduler} from './peanut-scheduler';
import {MeetingReport} from './report/meeting-report';
import {useGetPeanutSchedulerConfig} from './use-get-peanut-scheduler-config';

const CONTROL_BAR_BREAKPOINT = 1000;

type Props = ContextRouter & {
    isEmbedded: boolean;
    meeting: MeetingFragment;
    refetchMeeting: () => Promise<any>;
    activeTab: string;
    onSetSelectedTab: (newTab: string) => void;
    urlParams: ReportURLParams<'meeting'>;
    onParamChange: (arg1: any, arg2: boolean) => any;
};

export function MeetingPage(props: Props) {
    const updateMeeting = useUpdateMeeting();
    const {user} = useGetSessionUser();
    const {sessionInstance, isLoading: isSessionInstanceLoading} = useGetInstanceInfo();
    const hasFullAccess = sessionInstance
        ? sessionInstance.schedulerAccessType === 'FULL_ACCESS'
        : false;
    if (!isSessionInstanceLoading && !hasFullAccess && props.meeting.isManagedTemplate) {
        // if not full access, redirect to managed meetings page to see feature gate
        appHistory.push('/sales/scheduler/managed');
    }

    const {schedulerConfig, refetch: refetchSchedulerConfig} = useGetPeanutSchedulerConfig(
        props.meeting.modelId,
        NutshellFrontendSettings.instanceId
    );

    const tabsRef = React.useRef(null);
    const [width] = useResizeObserver(tabsRef.current);
    const hasHitBreakpoint = width && CONTROL_BAR_BREAKPOINT <= width;

    const hasTrackedReportTabView = React.useRef<boolean>(false);

    // Track the report tab view for analytics
    React.useEffect(() => {
        if (props.activeTab === 'report' && !hasTrackedReportTabView.current) {
            hasTrackedReportTabView.current = true;
            analyticsClient.track(analyticsClient.EVENTS.TRACK_REPORT_SCHEDULER_VIEW);
        }
    }, [props.activeTab]);

    const manageTab = props.meeting.isManagedTemplate
        ? {
              id: 'assignment',
              label: 'Assignment',
              icon: ContactsCircleIcon,
              href: '#',
              component: <Assignment meetingId={props.meeting.id} />,
          }
        : {
              id: 'bookings',
              label: 'Bookings',
              icon: BookingIcon,
              href: '#',
              // MCFX customers should not have access to native NS bookings
              externalLink: props.isEmbedded
                  ? 'https://app.webfx.com/marketingcloudfx/leadmanager/schedulerfx'
                  : undefined,
              component: (
                  <BookingsTable
                      isEmbedded={props.isEmbedded}
                      meeting={props.meeting}
                      // @ts-expect-error
                      bookings={props.meeting.bookings}
                      urlParams={props.urlParams}
                      onParamChange={props.onParamChange}
                  />
              ),
          };
    const tabOptions: Tab[] = [
        {
            id: 'details',
            label: 'Meeting details',
            icon: SchedulerIcon,
            href: '#',
            component: user && (
                <MeetingDetails
                    meeting={props.meeting}
                    meetingId={props.meeting.id}
                    meetingPreview={
                        schedulerConfig ? (
                            <PeanutScheduler
                                schedulerConfig={JSON.stringify(schedulerConfig)}
                                meetingModelId={props.meeting.modelId}
                            />
                        ) : (
                            <LoadingPage padding={0} />
                        )
                    }
                    refetchMeeting={props.refetchMeeting}
                    refetchSchedulerConfig={refetchSchedulerConfig}
                    user={user}
                />
            ),
        },
        {
            id: 'form',
            label: 'Form',
            icon: PencilToPaperIcon,
            href: '#',
            component: (
                <MeetingForm
                    meetingId={props.meeting.id}
                    isAdmin={Boolean(user && user.permissions.canAccessSetup)}
                    isManagedMeeting={props.meeting.isManagedMeeting}
                    managedByHtmlUrlPath={props.meeting.managedByHtmlUrlPath}
                    form={props.meeting.form}
                    publicFormId={props.meeting.form.publicFormId}
                    // @ts-expect-error
                    createRules={props.meeting.form.createRules}
                    isManagedTemplate={props.meeting.isManagedTemplate}
                />
            ),
        },
        {
            id: 'options',
            label: 'Advanced options',
            icon: SolidClockIcon,
            href: '#',
            component: (
                <AdvancedOptions
                    meeting={props.meeting}
                    meetingPreview={
                        schedulerConfig ? (
                            <PeanutScheduler
                                schedulerConfig={JSON.stringify(schedulerConfig)}
                                meetingModelId={props.meeting.modelId}
                            />
                        ) : (
                            <LoadingPage />
                        )
                    }
                    postSave={() => refetchSchedulerConfig()}
                />
            ),
        },
        manageTab,
        ...(NutClientConfig.hasSchedulerMeetingReportFF
            ? [
                  {
                      id: 'report',
                      label: 'Report',
                      icon: SidebarIconReportsIcon,
                      href: '#',
                      component: <MeetingReport meetingId={props.meeting.id} />,
                  },
              ]
            : []),
    ];

    const breadcrumbs = props.meeting.isManagedTemplate
        ? [{to: '/sales/scheduler/managed', text: 'Managed meetings'}]
        : [
              {
                  to: '/sales/scheduler',
                  text: 'Scheduler',
              },
          ];

    return (
        <PageContainer
            title='Meeting'
            noPadding={props.activeTab === 'bookings'}
            whiteBackground={props.activeTab !== 'bookings' && props.activeTab !== 'assignment'}
            pageTitleBar={
                <PageTitleBar
                    title={props.meeting.name}
                    subheaderPrimaryText=''
                    noMaxWidth={true}
                    buttons={
                        <MeetingPageButtons
                            meeting={props.meeting}
                            onUpdateIsHidden={(isHidden) => {
                                updateMeeting({
                                    id: props.meeting.id,
                                    isHidden,
                                });
                            }}
                            onUpdateIsActive={(isActive) => {
                                updateMeeting({
                                    id: props.meeting.id,
                                    isActive,
                                });
                            }}
                            activeTab={props.activeTab}
                        />
                    }
                    breadcrumbs={breadcrumbs}
                    editInPlaceProps={{
                        onChange: (newName: string | null | undefined) =>
                            updateMeeting({
                                id: props.meeting.id,
                                name: newName,
                            }).then(() => refetchSchedulerConfig()),
                        placeholder: 'Name this meeting',
                    }}
                />
            }
            controlBar={
                <PageControlBar
                    statusInfo={getStatusBarProps(props.meeting, Boolean(hasHitBreakpoint))}
                    tabs={{
                        selectedTab: props.activeTab,
                        options: tabOptions,
                        onChangeTab: (newTab) => {
                            const selectedTab = tabOptions.find((tab) => tab.id === newTab);
                            const externalLink = selectedTab && selectedTab.externalLink;
                            if (externalLink) {
                                window.open(externalLink, '_blank');

                                return;
                            }

                            props.onSetSelectedTab(newTab);
                            // We refetch the form when switching between tabs to be sure the
                            // fields are up to date
                            props.refetchMeeting();
                        },
                    }}
                    getRef={(ref) => {
                        // @ts-expect-error
                        tabsRef.current = ref;
                    }}
                />
            }
        >
            {/* @ts-expect-error */}
            <TabbedContent selectedTab={props.activeTab} tabOptions={tabOptions} />
        </PageContainer>
    );
}

function getStatusBarProps(meeting: MeetingFragment, hasHitBreakpoint: boolean) {
    if (meeting.isManagedTemplate && meeting.assignments) {
        const stats = meeting.assignments.stats;

        return {
            status: getStatusBadge(meeting),
            primaryText: getStatusBarPrimaryText(
                stats.assignedUsersCount,
                stats.assignedTeamsCount
            ),
            secondaryText:
                stats.assignedUsersCount > 0 || stats.assignedTeamsCount > 0
                    ? `${stats.severedCount} ${
                          stats.severedCount === 1
                              ? 'meeting has been modified'
                              : 'meetings have been modified'
                      }`
                    : null,
        };
    }

    return {
        status: getStatusBadge(meeting),
        primaryText: `${meeting.bookings.length} ${
            meeting.bookings.length === 1 ? 'booking' : 'bookings'
        }`,
        secondaryText: (
            <StatusBarSecondaryTimestamp
                datetime={meeting.lastBookedTime}
                prefix='Last booked'
                emptyText='Share meeting to begin collecting bookings'
                displayConfig={{
                    type: 'datetime',
                    format: !hasHitBreakpoint ? FormattedTimestamp.formats.monthDayYear : undefined,
                }}
            />
        ),
    };
}
